import React, { useEffect, useState } from 'react';
import { Storage, translate } from 'react-jhipster';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import {
  getEntitiesByBankId as getBookingConfirmationEmails,
  createEntity as createEmail,
  deleteEntity as deleteEmail,
} from 'app/entities/booking-confirmation-email/booking-confirmation-email.reducer';
import { Button } from 'reactstrap';
import Box from '@mui/material/Box';

const PaperStyled = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  ...theme.typography.body2,
}));

interface BookingConfirmationEmailSettingProps {
  bankId: number;
}

export function BookingConfirmationEmailSetting({ bankId }: BookingConfirmationEmailSettingProps) {
  const dispatch = useAppDispatch();
  const bookingConfirmationEmailList = useAppSelector(state => state.bookingConfirmationEmail.entities);

  const [confirmationEmailCollapse, setConfirmationEmailCollapse] = useState(true);
  const [addedEmails, setAddedEmails] = useState<string[]>([]);
  const [emailErrorText, setEmailErrorText] = useState('');
  const [removedEmailIds, setRemovedEmailIds] = useState<number[]>([]);
  const [inputEmail, setInputEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (bankId) {
      dispatch(getBookingConfirmationEmails(bankId));
    }
  }, [bankId]);

  const handleConfirmationEmailCollapse = () => {
    setConfirmationEmailCollapse(prev => !prev);
  };

  const handleSubmit = async () => {
    setIsSaving(true);

    try {
      // Delete removed emails
      for (const id of removedEmailIds) {
        await dispatch(deleteEmail(id));
      }

      // Create new emails
      for (const email of addedEmails) {
        const entity = {
          bankId,
          email,
        };
        await dispatch(createEmail(entity));
      }

      // Reset state after successful save
      setAddedEmails([]);
      setRemovedEmailIds([]);

      // Refresh the list
      await dispatch(getBookingConfirmationEmails(bankId));
    } catch (error) {
      console.error('Error saving booking confirmation emails:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTextInput = (email: string) => {
    setInputEmail(email);

    if (email === '') {
      setEmailErrorText('');
      return;
    }

    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    if (email.length < 6) {
      setEmailErrorText(translate('global.messages.validate.email.minlength'));
      return;
    }

    if (email.length > 253) {
      setEmailErrorText(translate('global.messages.validate.email.maxlength'));
      return;
    }

    if (!emailRegex.test(email)) {
      setEmailErrorText(translate('global.messages.validate.email.invalid'));
      return;
    }

    // Check if email already exists in DB list or added list
    const allEmails = [...bookingConfirmationEmailList.map(e => e.email), ...addedEmails];

    if (allEmails.includes(email)) {
      setEmailErrorText(translate('error.emailexists'));
      return;
    }

    setEmailErrorText('');
  };

  const handleAddCircleIcon = () => {
    if (inputEmail !== '' && emailErrorText === '') {
      setAddedEmails(prevAdded => [...prevAdded, inputEmail]);
      setInputEmail('');
      setEmailErrorText('');
    }
  };

  const handleDelete = (dataToDelete: string) => {
    // Check if it's in the added emails (not yet saved to DB)
    if (addedEmails.includes(dataToDelete)) {
      setAddedEmails(prevAdded => prevAdded.filter(e => e !== dataToDelete));
    } else {
      // It's from the DB, so need to delete it
      const emailToDelete = bookingConfirmationEmailList.find(e => e.email === dataToDelete);

      if (emailToDelete && emailToDelete.id) {
        setRemovedEmailIds(prevRemoved => [...prevRemoved, emailToDelete.id]);
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && inputEmail !== '' && emailErrorText === '') {
      handleAddCircleIcon();
    }
  };

  // Combine DB emails with newly added emails for display
  const displayEmails = [
    ...bookingConfirmationEmailList.filter(e => !removedEmailIds.includes(e.id)),
    ...addedEmails.map(email => ({ id: 0, bankId, email })),
  ];

  const hasChanges = addedEmails.length > 0 || removedEmailIds.length > 0;

  return (
    <div className="collapse-section">
      <Grid container className="align-items-center">
        <Grid item xs={10} sm={11} md={11.5}>
          <Typography variant="h5" component="div" data-cy="bookingConfirmationEmailHeading">
            {translate('comexApp.bank.details.bookingConfirmationEmail.title')}
          </Typography>
        </Grid>
        <Grid item xs={2} sm={1} md={0.5}>
          <IconButton aria-label="collapse" onClick={handleConfirmationEmailCollapse}>
            {confirmationEmailCollapse ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
          </IconButton>
        </Grid>
      </Grid>
      <Collapse in={confirmationEmailCollapse}>
        &nbsp;
        <Typography
          variant="body1"
          component={'div'}
          style={{ fontWeight: 500 }}
          align={Storage.session.get('locale') === 'ar-ly' ? 'right' : 'left'}
        >
          {translate('comexApp.bank.details.bookingConfirmationEmail.description')}
        </Typography>
        <PaperStyled elevation={0} className="my-3">
          <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ width: '100%' }}>
            <TextField
              id="userEmail"
              value={inputEmail}
              variant="standard"
              placeholder={translate('comexApp.bank.details.bookingConfirmationEmail.placeholder')}
              onChange={event => handleTextInput(event.target.value)}
              onKeyPress={handleKeyPress}
              error={emailErrorText !== ''}
              helperText={emailErrorText}
              sx={{
                marginTop: { md: '5px', sm: '-15px', xs: '10px' },
                marginLeft: '6px',
                marginRight: '6px',
                display: 'flex',
                flex: 1,
                minWidth: 0,
              }}
              className="col-sm-12"
            />
            <IconButton
              color="primary"
              aria-label="add user email"
              component="span"
              onClick={handleAddCircleIcon}
              disabled={inputEmail === '' || emailErrorText !== ''}
              sx={{ marginTop: { md: '-3px', sm: '-23px', xs: '3px' }, flexShrink: 0 }}
            >
              <AddCircleIcon fontSize="large" />
            </IconButton>
          </Stack>
          <div>
            {displayEmails.map((data, index) => {
              return (
                <Chip
                  key={data.id || `new-email-${index}`}
                  sx={{
                    margin: '6px 4px 6px 4px',
                    '& .MuiChip-deleteIcon': {
                      marginLeft: '2px',
                    },
                  }}
                  label={data.email}
                  onDelete={() => handleDelete(data.email)}
                />
              );
            })}
          </div>
        </PaperStyled>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" color="primary" onClick={handleSubmit} disabled={!hasChanges || isSaving}>
            {isSaving ? 'Saving...' : translate('comexApp.bank.details.update')}
          </Button>
        </Box>
      </Collapse>
    </div>
  );
}
